import dayjs from 'dayjs';
import commafy from 'commafy';

const formatAmount = (amount) => {
  if (amount == null || amount === '' || isNaN(parseFloat(amount))) {
    return null;
  }
  return parseFloat(amount).toFixed(2);
};

export const exportAccountStatementCSV = (data) => {
  if (!data) {
    console.error('No data provided for CSV export');
    return;
  }

  const csvRows = [];

  // CSV Header
  csvRows.push('Awake Interlink Account Statement');
  csvRows.push('');

  // Merchant Information
  if (data.merchant) {
    csvRows.push('Merchant Information');
    csvRows.push(`Name,${data.merchant.first_name} ${data.merchant.last_name}`);
    csvRows.push(`Email,${data.merchant.email}`);
    csvRows.push(`Company,${data.merchant.company_name || 'N/A'}`);
    csvRows.push(`Phone,${data.merchant.phone_number || 'N/A'}`);
    csvRows.push(`Company Address,${data.merchant.company_address || 'N/A'}`);
    csvRows.push(`Home Address,${data.merchant.address || 'N/A'}`);
    csvRows.push('');
  }

  // Statement Period
  if (data.period) {
    const startDate = data.period.start_date
      ? dayjs(data.period.start_date).format('MMM D, YYYY')
      : '-';
    const endDate = data.period.end_date
      ? dayjs(data.period.end_date).format('MMM D, YYYY')
      : '-';
    csvRows.push(`Statement Period,${startDate} - ${endDate}`);
    csvRows.push('');
  }

  // Balance Information
  if (data.balance) {
    csvRows.push('Balance Summary');
    csvRows.push(
      `Starting Balance,${commafy(formatAmount(data.balance.starting_balance))}`
    );
    csvRows.push(
      `Ending Balance,${commafy(formatAmount(data.balance.ending_balance))}`
    );
    csvRows.push('');
  }

  // Transaction Summary
  if (data.summary) {
    csvRows.push('Transaction Summary');
    csvRows.push(`Total Transactions,${data.summary.total_transactions}`);
    csvRows.push(
      `Successful Transactions,${data.summary.successful_transactions}`
    );
    csvRows.push(`Failed Transactions,${data.summary.failed_transactions}`);
    csvRows.push(`Pending Transactions,${data.summary.pending_transactions}`);
    csvRows.push(
      `Total Amount,${commafy(formatAmount(data.summary.total_amount))}`
    );
    csvRows.push(
      `Total Debit,${commafy(formatAmount(data.summary.total_debit))}`
    );
    csvRows.push(
      `Airtime Total,${commafy(formatAmount(data.summary.airtime_total))}`
    );
    csvRows.push(
      `Data Total,${commafy(formatAmount(data.summary.data_total))}`
    );
    csvRows.push(
      `Total Funding,${commafy(formatAmount(data.summary.total_funding))}`
    );
    csvRows.push(
      `Funding Transactions,${data.summary.total_funding_transactions}`
    );
    csvRows.push('');
  }

  // Funding History
  if (data.funding && data.funding.length > 0) {
    csvRows.push('Funding History');
    csvRows.push(
      'Type,Amount,Balance Before,Balance After,Status,Requested Date,Approved Date'
    );
    data.funding.forEach((item) => {
      const formattedBalanceBefore = formatAmount(item.balance_before);
      const formattedBalanceAfter = formatAmount(item.balance_after);
      const formattedAmount = formatAmount(item.amount);
      const balanceBefore =
        formattedBalanceBefore != null
          ? commafy(formattedBalanceBefore)
          : 'N/A';
      const balanceAfter =
        formattedBalanceAfter != null
          ? commafy(formattedBalanceAfter)
          : 'N/A';
      const amount =
        formattedAmount != null ? commafy(formattedAmount) : 'N/A';
      const requestedDate = item.requested_date
        ? dayjs(item.requested_date).format('MMM D, YYYY hh:mm A')
        : 'N/A';
      const approvedDate = item.approved_date
        ? dayjs(item.approved_date).format('MMM D, YYYY hh:mm A')
        : 'N/A';

      csvRows.push(
        `${item.transaction_type || 'N/A'},${amount},${balanceBefore},${balanceAfter},${
          item.status || 'N/A'
        },${requestedDate},${approvedDate}`
      );
    });
    csvRows.push('');
  }

  // Transactions
  if (data.transactions && data.transactions.length > 0) {
    csvRows.push('Transactions');
    csvRows.push(
      'Reference,Phone Number,Product,Type,Amount,Debit Amount,Balance Before,Balance After,Status,Date'
    );
    data.transactions.forEach((item) => {
      const formattedBalanceBefore = formatAmount(item.balance_before);
      const formattedBalanceAfter = formatAmount(item.balance_after);
      const formattedAmount = formatAmount(item.amount);
      const formattedDebitAmount = formatAmount(item.debit_amount);
      const balanceBefore =
        formattedBalanceBefore != null
          ? commafy(formattedBalanceBefore)
          : 'N/A';
      const balanceAfter =
        formattedBalanceAfter != null
          ? commafy(formattedBalanceAfter)
          : 'N/A';
      const amount =
        formattedAmount != null ? commafy(formattedAmount) : 'N/A';
      const debitAmount =
        formattedDebitAmount != null ? commafy(formattedDebitAmount) : 'N/A';
      const date = item.transaction_date
        ? dayjs(item.transaction_date).format('MMM D, YYYY hh:mm A')
        : 'N/A';

      csvRows.push(
        `${item.reference || 'N/A'},${item.phone_number || 'N/A'},${
          item.product_name || 'N/A'
        },${item.product_type || 'N/A'},${amount},${debitAmount},${balanceBefore},${balanceAfter},${
          item.status || 'N/A'
        },${date}`
      );
    });
    csvRows.push('');
  }

  // Footer
  csvRows.push(`Generated on,${dayjs().format('MMM D, YYYY hh:mm A')}`);

  // Create CSV content
  const csvContent = csvRows.join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute(
    'download',
    `Awake_Account_Statement_${dayjs().format('YYYY-MM-DD')}.csv`
  );
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
